import React, { useState, useEffect } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  MapPin, Calendar, Users, Plane, Award, CheckCircle, 
  ArrowRight, Star, Building, Home, BookOpen, Wifi, 
  Coffee, Dumbbell, Globe, ChevronRight, DollarSign,
  Clock, Shield, Target
} from 'lucide-react';
import schoolsData from '../data/schools.json';

// Icon mapping for facilities
const facilityIconMap = {
  'Simulators': Plane,
  'Library': BookOpen,
  'Hangar': Building,
  'Gym': Dumbbell,
  'Hostel': Home,
  'WiFi': Wifi,
  'Cafeteria': Coffee,
  'Swimming Pool': Dumbbell
};

const SchoolDetailTemplate = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedImage, setSelectedImage] = useState(0);

  const schoolSlug = location.pathname.split('/').filter(Boolean)[1];
  const schoolData = schoolsData.schools.find(school => school.slug === schoolSlug);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'facilities', 'courses', 'admissions'];
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!schoolData) {
    return <Navigate to="/schools" replace />;
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #ffffff;
          color: #1a202c;
        }
      `}</style>

      <PageWrapper style={{ opacity: isVisible ? 1 : 0 }}>
        {/* Hero Section */}
        <HeroSection bgImage={schoolData.hero_image}>
          <HeroOverlay />
          <HeroContent>
            <Breadcrumb>
              <Link to="/">Home</Link>
              <ChevronRight size={16} />
              <Link to="/schools">Schools</Link>
              <ChevronRight size={16} />
              <span>{schoolData.name}</span>
            </Breadcrumb>
            <HeroTitle>{schoolData.name}</HeroTitle>
            <HeroLocation>
              <span className="flag">{schoolData.location.flag}</span>
              <MapPin size={20} />
              {schoolData.location.city}, {schoolData.location.country}
            </HeroLocation>
            <BadgeContainer>
              {schoolData.badges.map(badge => (
                <Badge key={badge}>{badge}</Badge>
              ))}
            </BadgeContainer>
          </HeroContent>
        </HeroSection>

        {/* Sticky Navigation */}
        <StickyNav>
          <NavContainer>
            <NavLinks>
              <NavLink 
                active={activeSection === 'overview'} 
                onClick={() => scrollToSection('overview')}
              >
                Overview
              </NavLink>
              <NavLink 
                active={activeSection === 'facilities'} 
                onClick={() => scrollToSection('facilities')}
              >
                Fleet & Campus
              </NavLink>
              <NavLink 
                active={activeSection === 'courses'} 
                onClick={() => scrollToSection('courses')}
              >
                Courses
              </NavLink>
              <NavLink 
                active={activeSection === 'admissions'} 
                onClick={() => scrollToSection('admissions')}
              >
                Admissions
              </NavLink>
            </NavLinks>
            <ApplyButton to="/contactus">Apply Now</ApplyButton>
          </NavContainer>
        </StickyNav>

        {/* Stats Bar */}
        <StatsBar>
          <StatItem>
            <StatIcon><Calendar size={24} /></StatIcon>
            <StatLabel>Established</StatLabel>
            <StatValue>{schoolData.stats.founded}</StatValue>
          </StatItem>
          <StatItem>
            <StatIcon><Plane size={24} /></StatIcon>
            <StatLabel>Fleet Size</StatLabel>
            <StatValue>{schoolData.stats.fleet_size} Aircraft</StatValue>
          </StatItem>
          <StatItem>
            <StatIcon><Users size={24} /></StatIcon>
            <StatLabel>Graduates</StatLabel>
            <StatValue>{schoolData.stats.graduates}</StatValue>
          </StatItem>
          <StatItem>
            <StatIcon><Award size={24} /></StatIcon>
            <StatLabel>Training Type</StatLabel>
            <StatValue>Part 141 & 61</StatValue>
          </StatItem>
        </StatsBar>

        <MainContent>
          {/* Overview Section */}
          <Section id="overview">
            <SectionTitle>About {schoolData.name}</SectionTitle>
            <OverviewGrid>
              <AboutText>{schoolData.overview_text}</AboutText>
              <PriceCard>
                <PriceLabel>Starting From</PriceLabel>
                <PriceAmount>
                  {schoolData.starting_price.currency === 'USD' ? '$' : schoolData.starting_price.currency} 
                  {schoolData.starting_price.amount.toLocaleString()}
                </PriceAmount>
                <PriceNote>Comprehensive training package</PriceNote>
                <ContactButton to="/contactus">
                  Request Information
                  <ArrowRight size={18} />
                </ContactButton>
              </PriceCard>
            </OverviewGrid>
          </Section>

          {/* Facilities Section */}
          <Section id="facilities">
            <SectionTitle>Facilities & Features</SectionTitle>
            <FacilitiesGrid>
              {schoolData.features.map((feature, index) => {
                const IconComponent = facilityIconMap[feature] || Star;
                return (
                  <FacilityCard key={index}>
                    <FacilityIcon>
                      <IconComponent size={28} />
                    </FacilityIcon>
                    <FacilityName>{feature}</FacilityName>
                  </FacilityCard>
                );
              })}
            </FacilitiesGrid>
          </Section>

          {/* Gallery Section - Only show if images exist */}
          {schoolData.gallery_images && schoolData.gallery_images.length > 0 && (
            <Section>
              <SectionTitle>Campus Gallery</SectionTitle>
              <GalleryGrid>
                {schoolData.gallery_images.map((image, index) => (
                  <GalleryImage 
                    key={index}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img src={image} alt={`${schoolData.name} - ${index + 1}`} />
                  </GalleryImage>
                ))}
              </GalleryGrid>
            </Section>
          )}

          {/* Courses Section */}
          <Section id="courses">
            <SectionTitle>Available Courses</SectionTitle>
            <SectionSubtitle>
              Training programs offered at {schoolData.name}
            </SectionSubtitle>
            <CoursesGrid>
              {schoolData.courses_offered_ids.map(courseId => (
                <CourseCard key={courseId}>
                  <CourseIcon>
                    <Plane size={24} />
                  </CourseIcon>
                  <CourseInfo>
                    <CourseName>
                      {courseId === 'CRS-CPL-01' && 'Commercial Pilot License (CPL)'}
                      {courseId === 'CRS-PPL-02' && 'Private Pilot License (PPL)'}
                      {courseId === 'CRS-CFI-05' && 'Certified Flight Instructor (CFI)'}
                      {courseId === 'CRS-TYPE-320' && 'Airbus A320 Type Rating'}
                      {courseId === 'CRS-AME-06' && 'Aircraft Maintenance Engineering'}
                      {courseId === 'CRS-MER-03' && 'Multi-Engine Rating'}
                      {courseId === 'CRS-ATPL-04' && 'Airline Transport Pilot License'}
                      {courseId === 'CRS-CC-07' && 'Cabin Crew Training'}
                    </CourseName>
                    <CourseMeta>
                      <Clock size={14} />
                      12-15 Months
                      <span className="separator">•</span>
                      <DollarSign size={14} />
                      Contact for pricing
                    </CourseMeta>
                  </CourseInfo>
                  <ViewCourseLink to={`/courses`}>
                    View Details
                    <ArrowRight size={16} />
                  </ViewCourseLink>
                </CourseCard>
              ))}
            </CoursesGrid>
          </Section>

          {/* Admissions Section */}
          <Section id="admissions">
            <SectionTitle>Admission Process</SectionTitle>
            <AdmissionGrid>
              <AdmissionSteps>
                <StepCard>
                  <StepNumber>1</StepNumber>
                  <StepContent>
                    <StepTitle>Submit Application</StepTitle>
                    <StepDescription>
                      Complete our online application form with your personal details and educational background.
                    </StepDescription>
                  </StepContent>
                </StepCard>
                <StepCard>
                  <StepNumber>2</StepNumber>
                  <StepContent>
                    <StepTitle>Document Verification</StepTitle>
                    <StepDescription>
                      Submit required documents including medical certificates and educational transcripts.
                    </StepDescription>
                  </StepContent>
                </StepCard>
                <StepCard>
                  <StepNumber>3</StepNumber>
                  <StepContent>
                    <StepTitle>Interview & Assessment</StepTitle>
                    <StepDescription>
                      Participate in an admission interview and complete aptitude assessments.
                    </StepDescription>
                  </StepContent>
                </StepCard>
                <StepCard>
                  <StepNumber>4</StepNumber>
                  <StepContent>
                    <StepTitle>Enrollment Confirmation</StepTitle>
                    <StepDescription>
                      Receive your admission letter and complete enrollment formalities to secure your spot.
                    </StepDescription>
                  </StepContent>
                </StepCard>
              </AdmissionSteps>

              <AdmissionInfo>
                <InfoCard>
                  <InfoTitle>
                    <Shield size={20} />
                    Visa Support
                  </InfoTitle>
                  <InfoDescription>
                    We provide comprehensive visa assistance for international students. Our dedicated team will guide you through the entire visa application process.
                  </InfoDescription>
                  {schoolData.amenities.includes('visa_assistance') && (
                    <InfoBadge>Available</InfoBadge>
                  )}
                </InfoCard>

                <InfoCard>
                  <InfoTitle>
                    <Target size={20} />
                    Next Intake Dates
                  </InfoTitle>
                  <IntakeList>
                    <IntakeItem>
                      <CheckCircle size={16} />
                      <span>January 15, 2026</span>
                    </IntakeItem>
                    <IntakeItem>
                      <CheckCircle size={16} />
                      <span>April 1, 2026</span>
                    </IntakeItem>
                    <IntakeItem>
                      <CheckCircle size={16} />
                      <span>July 15, 2026</span>
                    </IntakeItem>
                    <IntakeItem>
                      <CheckCircle size={16} />
                      <span>October 1, 2026</span>
                    </IntakeItem>
                  </IntakeList>
                </InfoCard>
              </AdmissionInfo>
            </AdmissionGrid>
          </Section>

          {/* Map Section - Only show if coordinates exist */}
          {schoolData.coordinates && schoolData.coordinates.lat && (
            <Section>
              <SectionTitle>Explore the Area</SectionTitle>
              <MapContainer>
                <iframe
                  title="School Location"
                  width="100%"
                  height="450"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: '15px' }}
                  src={`https://www.google.com/maps?q=${schoolData.coordinates.lat},${schoolData.coordinates.lng}&hl=es&z=14&output=embed`}
                  allowFullScreen
                />
              </MapContainer>
            </Section>
          )}
        </MainContent>

        {/* CTA Section */}
        <CTASection>
          <CTAContent>
            <CTATitle>Ready to Start Your Aviation Journey?</CTATitle>
            <CTADescription>
              Join {schoolData.stats.graduates} successful graduates and take the first step towards your dream career in aviation.
            </CTADescription>
            <CTAButtons>
              <PrimaryCTA to="/contactus">
                Apply Now
                <ArrowRight size={20} />
              </PrimaryCTA>
              <SecondaryCTA to="/contactus">
                Schedule a Tour
              </SecondaryCTA>
            </CTAButtons>
          </CTAContent>
        </CTASection>
      </PageWrapper>
    </>
  );
};

// Styled Components
const PageWrapper = styled.div`
  transition: opacity 0.5s ease;
`;

const HeroSection = styled.section`
  position: relative;
  height: 600px;
  background: url(${props => props.bgImage}) center/cover no-repeat;
  display: flex;
  align-items: flex-end;
  padding: 60px 20px;

  @media (max-width: 768px) {
    height: 500px;
    padding: 40px 15px;
  }

  @media (max-width: 480px) {
    height: 400px;
    padding: 30px 15px;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, rgba(0, 31, 63, 0.3) 0%, rgba(0, 31, 63, 0.9) 100%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  color: white;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  margin-bottom: 20px;
  opacity: 0.9;

  a {
    color: white;
    text-decoration: none;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  span {
    opacity: 0.7;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 900;
  margin-bottom: 15px;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  line-height: 1.2;
`;

const HeroLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: clamp(1rem, 2vw, 1.2rem);
  margin-bottom: 20px;
  flex-wrap: wrap;

  .flag {
    font-size: 1.5rem;
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Badge = styled.span`
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #001f3f;
  padding: 8px 18px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 700;
  box-shadow: 0 4px 15px rgba(255, 165, 0, 0.4);
`;

const StickyNav = styled.nav`
  position: sticky;
  top: 80px;
  background: white;
  border-bottom: 2px solid #e9ecef;
  z-index: 90;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 60px;

  @media (max-width: 1024px) {
    padding: 10px 15px;
    min-height: 50px;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 5px;

  @media (max-width: 1024px) {
    gap: 3px;
  }
`;

const NavLink = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #0056b3, #003d82)' : 'transparent'};
  color: ${props => props.active ? 'white' : '#495057'};
  border: none;
  padding: 18px 28px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid ${props => props.active ? '#FFD700' : 'transparent'};
  white-space: nowrap;

  &:hover {
    color: ${props => props.active ? 'white' : '#0056b3'};
    background: ${props => props.active ? 'linear-gradient(135deg, #0056b3, #003d82)' : '#f8f9fa'};
  }

  @media (max-width: 1024px) {
    padding: 12px 16px;
    font-size: 0.85rem;
  }
`;

const ApplyButton = styled(Link)`
  padding: 12px 28px;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #001f3f;
  text-decoration: none;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 165, 0, 0.3);
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 165, 0, 0.5);
  }

  @media (max-width: 1024px) {
    padding: 10px 20px;
    font-size: 0.85rem;
  }
`;

const StatsBar = styled.div`
  max-width: 1400px;
  margin: 40px auto 60px;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;

  @media (max-width: 1024px) {
    margin: 30px auto 50px;
    gap: 15px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    margin: 25px auto 40px;
    padding: 0 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const StatItem = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 86, 179, 0.2);
  }
`;

const StatIcon = styled.div`
  color: #0056b3;
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 900;
  color: #001f3f;
`;

const MainContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px 20px 80px;

  @media (max-width: 768px) {
    padding: 15px 15px 60px;
  }

  @media (max-width: 480px) {
    padding: 12px 12px 50px;
  }
`;

const Section = styled.section`
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 900;
  color: #001f3f;
  margin-bottom: 15px;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #6c757d;
  margin-bottom: 40px;
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: start;

  @media (min-width: 1025px) {
    grid-template-columns: 2fr 1fr;
  }

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const AboutText = styled.p`
  font-size: 1.1rem;
  line-height: 1.9;
  color: #495057;
`;

const PriceCard = styled.div`
  background: linear-gradient(135deg, #f8fcff 0%, #e6f2ff 100%);
  border: 2px solid rgba(0, 86, 179, 0.2);
  border-radius: 20px;
  padding: 35px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 86, 179, 0.1);

  @media (min-width: 1025px) {
    position: sticky;
    top: 180px;
  }

  @media (max-width: 1024px) {
    padding: 30px;
  }

  @media (max-width: 640px) {
    padding: 25px;
  }
`;

const PriceLabel = styled.div`
  font-size: 0.95rem;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 10px;
`;

const PriceAmount = styled.div`
  font-size: 3rem;
  font-weight: 900;
  color: #0056b3;
  margin-bottom: 10px;
  line-height: 1;
`;

const PriceNote = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 25px;
`;

const ContactButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #0056b3, #003d82);
  color: white;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(0, 86, 179, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 86, 179, 0.5);
  }
`;

const FacilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FacilityCard = styled.div`
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 15px;
  padding: 30px 20px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: #0056b3;
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 86, 179, 0.15);
  }
`;

const FacilityIcon = styled.div`
  color: #0056b3;
  margin-bottom: 15px;
  display: flex;
  justify-content: center;
`;

const FacilityName = styled.div`
  font-weight: 700;
  color: #001f3f;
  font-size: 0.95rem;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const GalleryImage = styled.div`
  border-radius: 15px;
  overflow: hidden;
  height: 250px;
  cursor: pointer;
  transition: all 0.3s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

    img {
      transform: scale(1.1);
    }
  }
`;

const CoursesGrid = styled.div`
  display: grid;
  gap: 20px;
`;

const CourseCard = styled.div`
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 15px;
  padding: 25px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 20px;
  align-items: center;
  transition: all 0.3s ease;

  @media (min-width: 769px) {
    grid-template-columns: auto 1fr auto;
  }

  @media (max-width: 768px) {
    padding: 20px;
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 18px;
  }

  &:hover {
    border-color: #0056b3;
    box-shadow: 0 8px 25px rgba(0, 86, 179, 0.15);
    transform: translateX(5px);

    @media (max-width: 768px) {
      transform: translateY(-3px);
    }
  }
`;

const CourseIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #e6f2ff, #cce5ff);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0056b3;

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const CourseInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CourseName = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #001f3f;
`;

const CourseMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #6c757d;

  .separator {
    margin: 0 5px;
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ViewCourseLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #0056b3, #003d82);
  color: white;
  text-decoration: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(5px);
    box-shadow: 0 6px 20px rgba(0, 86, 179, 0.3);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const AdmissionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;

  @media (min-width: 1025px) {
    grid-template-columns: 2fr 1fr;
  }

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const AdmissionSteps = styled.div`
  display: grid;
  gap: 25px;
`;

const StepCard = styled.div`
  display: flex;
  gap: 20px;
  padding: 25px;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 15px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #0056b3;
    box-shadow: 0 8px 25px rgba(0, 86, 179, 0.1);
    transform: translateX(5px);
  }
`;

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0056b3, #003d82);
  color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 900;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #001f3f;
`;

const StepDescription = styled.div`
  font-size: 0.95rem;
  color: #6c757d;
  line-height: 1.7;
`;

const AdmissionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #f8fcff, #e6f2ff);
  border: 2px solid rgba(0, 86, 179, 0.2);
  border-radius: 15px;
  padding: 25px;
`;

const InfoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #001f3f;
  margin-bottom: 15px;
`;

const InfoDescription = styled.p`
  font-size: 0.95rem;
  color: #495057;
  line-height: 1.7;
  margin-bottom: 15px;
`;

const InfoBadge = styled.span`
  display: inline-block;
  padding: 6px 14px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
`;

const IntakeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IntakeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  color: #495057;
  font-weight: 600;

  svg {
    color: #10b981;
    flex-shrink: 0;
  }
`;

const MapContainer = styled.div`
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #001f3f 0%, #0056b3 50%, #003d82 100%);
  padding: 80px 20px;
  text-align: center;
  color: white;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  margin-bottom: 20px;
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 40px;
  opacity: 0.95;
  line-height: 1.7;
`;

const CTAButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const PrimaryCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 18px 40px;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #001f3f;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 800;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(255, 165, 0, 0.4);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(255, 165, 0, 0.6);
  }
`;

const SecondaryCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 18px 40px;
  background: transparent;
  color: white;
  text-decoration: none;
  border: 2px solid white;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    color: #0056b3;
    transform: translateY(-3px);
  }
`;

export default SchoolDetailTemplate;
